import { AppContainer } from './AppContainer.js';
import { css, html, LitElement } from 'lit';
import { initializeCommandEngine } from '../commands/CommandEngine.js';
import SnapshotController from '../snapshot/SnapshotController.js';
import alertStore from './AlertStore.js';
import { setAlert } from './AlertSlice.js';

// 各種Store/Sliceのインポート
import appSettingStore from './AppSettingStore.js';
import { loadAppSettingData } from './AppSettingSlice.js';
import editorJsonVersionStore from './EditorJsonVersionStore.js';
import { loadCurrentVersion } from './EditorJsonVersionSlice.js';
import userSettingStore from './UserSettingStore.js';
import { setAiCodingEnabled, setUserGeminiApiKeyRegistered } from './UserSettingSlice.js';
import templateSelectedModifierItemStore from './TemplateSelectedModifierItemStore.js';
import { loadTemplateSelectedModifierItemData } from './TemplateSelectedModifierItemSlice.js';
import templateJsonStore from './TemplateJsonStore.js';
import { loadTemplateJson } from './TemplateJsonSlice.js';
import templateSelectedRootStore from './TemplateSelectedRootStore.js';
import { resetRoot } from './TemplateSelectedRootSlice.js';
import templateSelectedItemStore from './TemplateSelectedItemStore.js';
import { deselectItem } from './TemplateSelectedItemSlice.js';
import attributeItemStore from './AttributeItemStore.js';
import { loadAttributeItemData } from './AttributeItemSlice.js';
import eventItemStore from './EventItemStore.js';
import { loadEventItemData } from './EventItemSlice.js';
import toolBoxItemStore from './ToolBoxItemStore.js';
import { loadToolBoxData, loadToolBoxCustomData } from './ToolBoxItemSlice.js';
import scriptItemStore from './ScriptItemStore.js';
import { loadScriptData } from './ScriptItemSlice.js';
import platformDeviceStore from './PlatformDeviceStore.js';
import { loadPlatformDevice } from './PlatformDeviceSlice.js';
import serviceWorkerItemStore from './ServiceWorkerItemStore.js';
import { loadServiceWorkerItemData } from './ServiceWorkerItemSlice.js';
import testItemStore from './TestItemStore.js';
import { loadTestData } from './TestItemSlice.js';

export class DemoAppContainer extends AppContainer {

    static styles = [
        AppContainer.styles,
        css`
            .demo-badge {
                position: fixed;
                bottom: 10px;
                left: 10px;
                background: #ff9800;
                color: white;
                padding: 5px 10px;
                border-radius: 5px;
                z-index: 9999;
                font-weight: bold;
                pointer-events: none;
                opacity: 0.8;
                font-size: 12px;
                box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            }
        `
    ];

    constructor() {
        super();
        this.disableCloseWarning();
    }

    async connectedCallback() {
        // 親クラス(AppContainer)のサーバー通信処理を回避するため、
        // LitElementのconnectedCallbackを直接呼び出します。
        if (LitElement.prototype.connectedCallback) {
            LitElement.prototype.connectedCallback.call(this);
        }

        // 画面全体のスワイプリロードを禁止
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';

        this.boundHandleOpenTutorial = this.handleOpenTutorial.bind(this);
        this.boundHandleRestartTour = this.triggerTourGuide.bind(this);
        window.addEventListener('open-tutorial', this.boundHandleOpenTutorial);
        window.addEventListener('restart-tour', this.boundHandleRestartTour);

        // スターターテンプレートとAIエージェント起動の中継（デモ版用）
        this.boundHandleApplyStarterTemplate = (e) => {
            const templateContainer = this.shadowRoot.querySelector('template-container');
            if (templateContainer && typeof templateContainer.applyStarterTemplate === 'function') {
                templateContainer.applyStarterTemplate(e.detail.type);
            }
        };
        window.addEventListener('apply-starter-template', this.boundHandleApplyStarterTemplate);

        this.boundHandleOpenAIAgentWindow = () => {
            const agentWindow = this.shadowRoot.querySelector('agent-chat-window');
            if (agentWindow) {
                agentWindow.show();
            }
        };
        window.addEventListener('open-ai-agent-window', this.boundHandleOpenAIAgentWindow);

        // 1. グローバルD&Dリスナー登録（エッジ表示用）
        window.addEventListener('app-drag-start', this._boundHandleAppDragStart);
        window.addEventListener('app-drag-end', this._boundHandleAppDragEnd);
        window.addEventListener('touchmove', this._boundHandleGlobalDragMove, { passive: false });
        window.addEventListener('mousemove', this._boundHandleGlobalDragMove, { passive: false });

        // 2. ピンチズーム無効化機能の復元
        this._blockNativeZoom = (e) => {
            if (e.scale !== 1) e.preventDefault();
        };
        window.addEventListener('gesturestart', this._blockNativeZoom, { passive: false });
        window.addEventListener('gesturechange', this._blockNativeZoom, { passive: false });

        this._blockNativeZoomTouches = (e) => {
            if (e.touches.length >= 2) e.preventDefault();
        };
        window.addEventListener('touchstart', this._blockNativeZoomTouches, { passive: false });

        this._blockNativeZoomWheel = (e) => {
            if (e.ctrlKey) e.preventDefault();
        };
        window.addEventListener('wheel', this._blockNativeZoomWheel, { passive: false });

        // --- 1. モジュールの動的インポート ---
        let promises = [];
        promises.push(import('./DemoAgentChatWindow.js'));
        promises.push(import('./DemoPlatformBottomManu.js'));
        promises.push(import('./DemoPlatformDeviceQrCode.js'));
        promises.push(import('./SnapshotManager.js'));
        promises.push(import('./AiCoderWindow.js'));
        promises.push(import('./FileExplorer.js'));
        promises.push(import('./AppMenuContainer.js'));
        promises.push(import('./AppHeaderContainer.js'));
        promises.push(import('./ToolBox.js'));
        promises.push(import('./ToolBoxItemEditor.js'));
        promises.push(import('./PlatformBackground.js'));
        promises.push(import('./PlatformContainer.js'));
        promises.push(import('./AppContainerLoadingOverlay.js'));

        // --- 2. Storeの参照をインスタンスに保持 ---
        this.appSettingStore = appSettingStore;
        this.editorJsonVersionStore = editorJsonVersionStore;
        this.userSettingStore = userSettingStore;
        this.templateSelectedModifierItemStore = templateSelectedModifierItemStore;
        this.templateJsonStore = templateJsonStore;
        this.templateSelectedRootStore = templateSelectedRootStore;
        this.templateSelectedItemStore = templateSelectedItemStore;
        this.attributeItemStore = attributeItemStore;
        this.eventItemStore = eventItemStore;
        this.toolBoxItemStore = toolBoxItemStore;
        this.scriptItemStore = scriptItemStore;
        this.platformDeviceStore = platformDeviceStore;
        this.serviceWorkerItemStore = serviceWorkerItemStore;
        this.testItemStore = testItemStore;

        // 子コンポーネントのインポート待機
        await this.importPlatformSwitcher();
        await this.importPropertyContainer();
        await this.importScriptContainer();
        await Promise.all(promises);

        // --- 3. コマンドエンジンの初期化 ---
        initializeCommandEngine({
            templateJsonStore: this.templateJsonStore,
            scriptItemStore: this.scriptItemStore,
            eventItemStore: this.eventItemStore,
            appSettingStore: this.appSettingStore,
            attributeItemStore: this.attributeItemStore,
            toolBoxItemStore: this.toolBoxItemStore,
            serviceworkerItemStore: this.serviceWorkerItemStore,
        });

        // --- 4. データの初期ロード ---
        this.loading = true;
        try {
            // 各ストアの初期化
            this.appSettingStore.dispatch(loadAppSettingData());
            this.attributeItemStore.dispatch(loadAttributeItemData());
            this.eventItemStore.dispatch(loadEventItemData());
            this.scriptItemStore.dispatch(loadScriptData());
            this.serviceWorkerItemStore.dispatch(loadServiceWorkerItemData());
            this.toolBoxItemStore.dispatch(loadToolBoxData());
            this.templateSelectedModifierItemStore.dispatch(loadTemplateSelectedModifierItemData());
            this.platformDeviceStore.dispatch(loadPlatformDevice());
            this.testItemStore.dispatch(loadTestData());

            // デモモード設定
            this.userSettingStore.dispatch(setAiCodingEnabled(true));
            this.userSettingStore.dispatch(setUserGeminiApiKeyRegistered(false));

            // ユーザー定義スクリプトの同期
            this.syncAllUserScriptsToToolbox();

            this.setAttribute('is-demo', 'true');

            SnapshotController.identKey = "demo_user";
            SnapshotController.appKey = "demo_app";
            SnapshotController.version = "1.0.0";

            // スナップショットのクリア
            await SnapshotController.clearSnapshotsForCurrentProject();

            // 選択状態のリセット
            this.templateSelectedRootStore.dispatch(resetRoot());
            this.templateSelectedItemStore.dispatch(deselectItem());

            localStorage.removeItem('pwappy_tour_completed');

            this.initEditorDisplay();

            // デモ版起動時の処理（スターターモーダル & ツアー）
            const isAppEmpty = this.templateJsonStore.getState().templateJson.templates[0].children.length === 0;

            setTimeout(() => {
                if (isAppEmpty) {
                    const starterModal = this.shadowRoot.querySelector('starter-template-modal');
                    if (starterModal) {
                        // モーダルが閉じた後にデモツアーを開始する
                        // イベントオブジェクトを受け取り、AIエージェント選択時はツアー開始を保留します
                        starterModal.addEventListener('starter-modal-closed', (e) => {
                            setTimeout(() => {
                                if (e.detail?.selectedType === 'ai') {
                                    return;
                                }
                                this.triggerTourGuide();
                            }, 500);
                        }, { once: true });
                        starterModal.show();
                    }
                } else {
                    this.triggerTourGuide();
                }
            }, 500);

        } catch (e) {
            console.error("Demo initialization failed", e);
        } finally {
            this.loading = false;
        }
    }

    // リスナー解除処理のオーバーライド
    disconnectedCallback() {
        window.removeEventListener('open-tutorial', this.boundHandleOpenTutorial);
        window.removeEventListener('restart-tour', this.boundHandleRestartTour);

        // リスナー解除
        window.removeEventListener('apply-starter-template', this.boundHandleApplyStarterTemplate);
        window.removeEventListener('open-ai-agent-window', this.boundHandleOpenAIAgentWindow);

        if (this._blockNativeZoomTouches) {
            window.removeEventListener('touchstart', this._blockNativeZoomTouches);
        }
        if (this._blockNativeZoomWheel) {
            window.removeEventListener('wheel', this._blockNativeZoomWheel);
        }

        // super.disconnectedCallback() の中で、AppContainer 側の解除処理（app-drag-start 等）は呼び出されます
        super.disconnectedCallback();
    }

    // --- 5. サーバー通信機能の無効化 ---

    async handleEditorJsonSaveEvent(event) {
        alertStore.dispatch(setAlert('デモモードのため保存はできません。\nリロードすると初期状態に戻ります。'));
        return Promise.resolve();
    }

    async handleEditorJsonSaveAndCloseEvent(event) {
        alertStore.dispatch(setAlert('デモモードのため保存はできません。'));
    }

    handleOpenAICoderWindow(e) {
        alertStore.dispatch(setAlert('デモモードではAIコーディング機能は利用できません。'));
    }

    handleOpenAgentWindow() {
        super.handleOpenAgentWindow();
    }

    handleShowFileExplorerEvent(e) {
        alertStore.dispatch(setAlert('デモモードではファイル管理機能は利用できません。'));
    }

    render() {
        return html`
            ${super.render()}
            <div class="demo-badge">DEMO MODE</div>
        `;
    }
}

customElements.define('app-container', DemoAppContainer);